import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "components/UserContext";
import { AiFillCaretDown, AiFillCaretUp } from "react-icons/ai";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";
import { Menu, Tooltip } from "antd";
import {
  FaChartLine,
  FaComments,
  FaDatabase,
  FaTableCellsLarge,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa6";
import * as fa6 from "react-icons/fa6";
import { ProduceJWT } from "jose";
import { motion } from "framer-motion";

const generalItems = [
  {
    key: "general-group",
    label: "General",
    type: "group",
    children: [
      {
        key: "faq",
        label: "FAQ",
        title: "FAQ",
        icon: <FaComments alt="FAQ" />,
      },
    ],
  },
];

const defaultItems = [
  {
    key: "dashboard",
    label: "Dashboard",
    title: "Dashboard",
    icon: <FaTableCellsLarge alt="Dashboard" />,
  },
];

function Sidebar() {
  const navigate = useNavigate();
  const { userDetails } = useUser();
  const [menuItems, setMenuItems] = useState([
    ...defaultItems,
    ...generalItems,
  ]);
  const [adminMenus, setAdminMenus] = useState(null);
  const [selectedMenuKey, setSelectedMenuKey] = useState("dashboard");
  const [openMenuKeys, setOpenMenuKeys] = useState([]);

  // Sidebar collapse state - load from localStorage or default to false (expanded)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });

  const [reports, setReports] = useState([]);
  const location = useLocation();
  const { hash, pathname, search } = location;

  // Save collapse state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isCollapsed);
    // Dispatch custom event to notify PageLayout of sidebar width change
    window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: { isCollapsed } }));
  }, [isCollapsed]);

  const fetchReports = () => {
    if (userDetails && userDetails.IsOrgUser === false) {
      const fetchStartTime = Date.now();

      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log('│ [Sidebar] Fetching PowerBI Reports List                    │');
      console.log('└─────────────────────────────────────────────────────────────┘');
      console.log('[Sidebar] User is not an org user - fetching reports');
      console.log('[Sidebar] Calling /pbi/get-all-reports endpoint...');

      execute(
        "POST",
        `${protectedResources.api.baseEndPoint}/pbi/get-all-reports`
      )
        .then((response) => {
          const fetchDuration = Date.now() - fetchStartTime;

          console.log(`[Sidebar] ✓ Reports fetched successfully (${fetchDuration}ms)`);
          console.log('[Sidebar] Raw response data count:', response.data?.length || 0);

          var fetchedReports = response.data
            .filter((r) => r.embedToken)
            .map((r) => ({
              ...r,
              module: r.name.split("-")[0]?.trim(),
              name: r.name.split("-")[1]?.trim(),
            }));

          console.log(`[Sidebar] Filtered reports (with embed token): ${fetchedReports.length}`);

          if (fetchedReports.length > 0) {
            console.log('[Sidebar] Reports:', fetchedReports.map(r => ({
              id: r.id,
              name: r.name,
              module: r.module,
              hasToken: !!r.embedToken,
              tokenExpiration: r.tokenExpiration
            })));

            // Show token expiration info
            fetchedReports.forEach(report => {
              if (report.tokenExpiration) {
                const expiryDate = new Date(report.tokenExpiration);
                const minutesUntilExpiry = Math.round((expiryDate - new Date()) / 1000 / 60);
                console.log(`[Sidebar]   - ${report.module}-${report.name}: token expires in ${minutesUntilExpiry} minutes (${expiryDate.toLocaleTimeString()})`);
              }
            });

            console.log('[Sidebar] ⚠️  NOTE: These tokens are cached in Sidebar state');
            console.log('[Sidebar] ⚠️  They will NOT be passed to Report component (prevents stale tokens)');
          }

          setReports(fetchedReports);
          console.log('[Sidebar] State updated with reports');
        })
        .catch((error) => {
          const fetchDuration = Date.now() - fetchStartTime;
          console.error('[Sidebar] ✗ Exception while fetching reports (' + fetchDuration + 'ms)');
          console.error('[Sidebar] Error:', error);
          console.error('[Sidebar] Stack:', error.stack);
        });
    } else {
      if (!userDetails) {
        console.log('[Sidebar] Skipping report fetch - user details not loaded yet');
      } else if (userDetails.IsOrgUser) {
        console.log('[Sidebar] Skipping report fetch - user is an org user (org users don\'t have reports)');
      }
    }
  };

  const loadAdminMenus = () => {
    var adminMenus = [];
    if (userDetails?.IsAdmin) {
      adminMenus = {
        key: "admin-group",
        label: "Admin",
        type: "group",
        children: [
          {
            label: "User Management",
            key: "UM",
            title: "User Management",
            icon: <FaUsers alt="User Management" />,
          },
           // Issue 11: Data Administration hidden from sidebar
          // {
          //   label: "Data Administration",
          //   key: "DA",
          //   icon: <FaDatabase alt="Data Administration" />,
          // },
        ],
      };
      setAdminMenus(adminMenus);
      setMenuItems([...defaultItems, adminMenus, ...generalItems]);
    }
  };

  const setCurrentMenuActive = () => {
    if (pathname.startsWith("/report/")) {
      const parts = pathname.split("/");
      //console.log('setCurrentMenuActive:', parts);
      if (parts.length >= 3) {
        const moduleShortName = parts[2];
        const reportId = parts[3];
        //console.log('setCurrentMenuActive:', moduleShortName, reportId);
        setOpenMenuKeys([moduleShortName]);
        setSelectedMenuKey(reportId);
        return;
      }
    }
    switch (pathname) {
      case "/dashboard":
        setSelectedMenuKey("dashboard");
        break;
      case "/userManage":
        setSelectedMenuKey("UM");
        break;
      case "/data-administration":
        setSelectedMenuKey("DA");
        break;
      case "/faq":
        setSelectedMenuKey("faq");
        break;
      default:
        //console.log('default:', pathname, location);
        break;
    }
  };

  useEffect(() => {
    setMenuItems([...defaultItems, ...generalItems]);
    loadAdminMenus();
    fetchReports();
    setCurrentMenuActive();
  }, [userDetails]);

  const getIcon = (Icon) => <Icon />;

  useEffect(() => {
    if (reports.length > 0 && userDetails?.Modules) {
      const moduleMenus = {
        key: "module-group",
        label: "Modules",
        type: "group",
        children: userDetails.Modules.map((module) => {
          const moduleReports = reports.filter(
            (r) => r.module === module.ShortName
          );
          //console.log("moduleReports:", module.ShortName, moduleReports, reports);
          if (moduleReports.length === 0) {
            return null;
          }
          const moduleIcon = isCollapsed ? (
            <Tooltip title={module.ModuleName} placement="right">
              <span>{getIcon(fa6[module.Icon])}</span>
            </Tooltip>
          ) : (
            getIcon(fa6[module.Icon])
          );

          return {
            label: module.ModuleName,
            key: module.ShortName,
            title: module.ModuleName,
            // icon: <module.Icon alt={module.ModuleName} /> ,
            icon: moduleIcon,
            onTitleClick: () => {
              // Expand sidebar when module is clicked in collapsed state
              if (isCollapsed) {
                setIsCollapsed(false);
              }
            },
            children: moduleReports.map((report) => {
              return {
                label: report.name,
                key: report.id,
                title: report.name,
                icon: <FaChartLine alt={report.name} />,
                onClick: () =>
                  handleReportClick(
                    report.id,
                    null, // embedUrl - not needed, Report component will fetch
                    null, // accessToken - not needed, Report component will fetch
                    null, // isEmbedToken - not needed, Report component will fetch
                    null, // tokenExpiration - not needed, Report component will fetch
                    report.module
                  ),
              };
            }),
          };
        }).filter((module) => module !== null),
      };
      setMenuItems([...defaultItems, adminMenus, moduleMenus, ...generalItems]);
    }
  }, [userDetails, reports, isCollapsed]);

  const handleReportClick = (
    reportId,
    embedUrl,
    accessToken,
    isEmbedToken,
    tokenExpiration,
    moduleShortName
  ) => {
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('[Sidebar] Report clicked:', reportId);
    console.log('[Sidebar] Module:', moduleShortName);
    console.log('[Sidebar] Cached data available:', {
      hasEmbedUrl: !!embedUrl,
      hasAccessToken: !!accessToken,
      tokenExpiration: tokenExpiration || 'none',
      isEmbedToken
    });

    // Check if cached token is expired (for logging purposes only - we don't use it)
    if (tokenExpiration) {
      const now = new Date();
      const expiryDate = new Date(tokenExpiration);
      const timeUntilExpiry = expiryDate.getTime() - now.getTime();
      const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);

      if (timeUntilExpiry <= 0) {
        console.log('[Sidebar] ⚠️  Cached token is EXPIRED (expired ' + Math.abs(minutesUntilExpiry) + ' minutes ago)');
        console.log('[Sidebar] ✓ Good thing we\'re not using it! Report component will fetch fresh token.');
      } else {
        console.log('[Sidebar] ℹ️  Cached token still valid for ' + minutesUntilExpiry + ' minutes');
        console.log('[Sidebar] ✓ But we\'re not using it - Report component will fetch fresh token anyway.');
      }
    }

    setSelectedMenuKey(reportId);

    // CRITICAL FIX: Don't pass cached tokens via location.state
    // The Report component will fetch fresh tokens via fetchInitialReportData()
    // This prevents using stale/expired tokens from Sidebar's cached state
    console.log('[Sidebar] ✓ Navigating without passing tokens (prevents stale token usage)');
    console.log('[Sidebar] Target route:', `/report/${moduleShortName}/${reportId}`);
    console.log('[Sidebar] Report component will fetch fresh token from backend');
    console.log('═══════════════════════════════════════════════════════════════');

    navigate(`/report/${moduleShortName}/${reportId}`);
  };

  const onMenuItemClicked = (e) => {
    //console.log('menu item clicked:', e);
    switch (e.key) {
      case "dashboard":
        setSelectedMenuKey("dashboard");
        navigate("/");
        break;
      case "UM":
        setSelectedMenuKey("UM");
        navigate("/userManage");
        break;
      case "DA":
        setSelectedMenuKey("DA");
        navigate("/data-administration");
        break;
      case "faq":
        setSelectedMenuKey("faq");
        navigate("/faq");
        break;
      // case "CM":
      //     navigate('/inspection');
      //     break;
      // case "Citation":
      //     navigate('/citation-management');
      //     break;
      // case "Inspection":
      //     navigate('/inspection');
      //     break;
      default:
        break;
    }
  };
  return (
    <>
      <motion.div
        initial={false}
        animate={{ width: isCollapsed ? 80 : 270 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="flex overflow-hidden z-10 h-[calc(100vh-70px)] flex-col fixed bg-[#fcfcfc] border-white"
      >
        <aside className="flex flex-col h-full relative">
          {/* Header with logo */}
          <header className="flex items-center text-[#002c57] text-xl gap-[9px] p-4 bg-white border-r-[1px] border-[#e5e7eb] relative">
          {/* Logo - only show when expanded */}
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2 overflow-hidden"
            >
              {!process.env.REACT_APP_HIDE_LOGO && (
                <img
                  className="aspect-square object-cover object-center w-[53px]"
                  src="https://birddata.blob.core.windows.net/b2clogin/cust-app/images/prism-logo.png"
                  alt={process.env.REACT_APP_APPNAME}
                />
              )}
              <div className="flex text-center ml-2 mx-0">
                <img
                  className="w-28 object-contain"
                  src="/logo.png"
                  alt="logo"
                />
              </div>
            </motion.div>
          )}

          </header>

        {/* Sidebar menu with scrollbar */}
        <div className="flex-grow overflow-y-auto overflow-x-hidden">
          <Menu
            items={menuItems}
            mode="inline"
            selectedKeys={[selectedMenuKey]}
            defaultSelectedKeys={["dashboard"]}
            onClick={onMenuItemClicked}
            inlineCollapsed={isCollapsed}
            openKeys={isCollapsed ? [] : openMenuKeys}
            onOpenChange={(keys) => setOpenMenuKeys(keys)}
          />
        </div>
        </aside>
      </motion.div>
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="fixed z-20 top-[96px] h-8 w-8 rounded-full border border-[#e5e7eb] bg-white text-[#002c57] shadow-sm flex items-center justify-center transition-all duration-200"
        style={{ left: isCollapsed ? 62 : 252 }}
      >
        {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
      </button>
    </>
  );
}

export default Sidebar;
